/**
 * User Settings Page
 * Tabs: Profile, Security, Payment Methods, Notifications
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  User, Lock, CreditCard, Bell, Eye, EyeOff, Plus, Trash2,
  Star, Shield, Monitor, Smartphone, Laptop, LogOut, Building2,
  ChevronRight, Save, X, DollarSign, Clock, Calendar, Info,
  Mail, CheckCircle, AlertCircle, MoreHorizontal, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import BankingPaymentsTab from '../components/payments/BankingPaymentsTab';
import { NotificationChannelsCard } from '../components/comm/NotificationChannelsCard';
import { GmailSyncButton } from '../components/comm/GmailSync';
import { GoogleRedirectInfo } from '../components/settings/GoogleRedirectInfo';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'security', label: 'Security', icon: Lock },
  { id: 'email', label: 'Email Accounts', icon: Mail },
  { id: 'banking', label: 'Banking & Payments', icon: DollarSign },
  { id: 'payment', label: 'Payment Methods', icon: CreditCard },
  { id: 'notifications', label: 'Notifications', icon: Bell },
];

export default function UserSettingsPage() {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'profile';
  });

  // Handle OAuth callback redirects
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'gmail_connected') {
      toast.success('Gmail account connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('error')) {
      toast.error(`Gmail connection failed: ${params.get('error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="user-settings-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Settings</h1>
          <span className="text-xs bg-blue-100 text-blue-800 font-medium px-3 py-1 rounded-full">
            {user?.role === 'iso_admin' ? 'ISO Admin' : user?.role === 'iso_agent' ? 'Agent' : 'User'} View
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-card border rounded-xl overflow-hidden" data-testid="settings-nav">
            <div className="p-4 border-b">
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Settings</p>
            </div>
            <nav className="p-2">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-0.5 ${
                      isActive
                        ? 'text-primary bg-blue-50'
                        : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                    }`}
                    data-testid={`settings-tab-${tab.id}`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3 space-y-6">
          {activeTab === 'profile' && <ProfileTab />}
          {activeTab === 'security' && <SecurityTab />}
          {activeTab === 'email' && <EmailAccountsTab />}
          {activeTab === 'banking' && <BankingPaymentsTab />}
          {activeTab === 'payment' && <PaymentTab />}
          {activeTab === 'notifications' && <NotificationsTab />}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════
   PROFILE TAB
   ═══════════════════════════════════════════ */
function ProfileTab() {
  const { user, loadUser } = useAuthStore();
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '', job_title: '', bio: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      const names = (user.full_name || '').split(' ');
      setForm({
        first_name: user.first_name || names[0] || '',
        last_name: user.last_name || names.slice(1).join(' ') || '',
        phone: user.phone || '',
        job_title: user.job_title || '',
        bio: user.bio || '',
      });
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.request('/user-settings/profile', {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      toast.success('Profile updated successfully');
      loadUser();
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    }
    setSaving(false);
  };

  const initials = ((form.first_name?.[0] || '') + (form.last_name?.[0] || '')).toUpperCase() || 'U';

  return (
    <div className="bg-card border rounded-xl" data-testid="profile-tab">
      <div className="p-6 border-b">
        <h3 className="text-lg font-semibold">Profile Information</h3>
        <p className="text-sm text-muted-foreground mt-1">Update your personal information and contact details.</p>
      </div>
      <div className="p-6 space-y-6">
        {/* Avatar */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <span className="text-xl font-bold text-primary">{initials}</span>
          </div>
          <div>
            <p className="text-sm font-medium">{form.first_name} {form.last_name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label>First Name</Label>
            <Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="mt-1.5" data-testid="profile-first-name" />
          </div>
          <div>
            <Label>Last Name</Label>
            <Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="mt-1.5" data-testid="profile-last-name" />
          </div>
          <div>
            <Label>Email Address</Label>
            <Input value={user?.email || ''} disabled className="mt-1.5 bg-muted/30" />
            <p className="text-xs text-muted-foreground mt-1">Contact admin to change email</p>
          </div>
          <div>
            <Label>Phone Number</Label>
            <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+1 (555) 123-4567" className="mt-1.5" data-testid="profile-phone" />
          </div>
          <div>
            <Label>Job Title</Label>
            <Input value={form.job_title} onChange={e => setForm({...form, job_title: e.target.value})} placeholder="Sales Representative" className="mt-1.5" data-testid="profile-job-title" />
          </div>
        </div>
        <div>
          <Label>Bio</Label>
          <textarea
            value={form.bio}
            onChange={e => setForm({...form, bio: e.target.value})}
            placeholder="Tell us about yourself..."
            rows={3}
            className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            data-testid="profile-bio"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={() => window.location.reload()}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-profile-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════
   EMAIL ACCOUNTS TAB
   ═══════════════════════════════════════════ */

const SMTP_PROVIDERS = [
  { id: 'gmail_smtp', label: 'Gmail (SMTP)', host: 'smtp.gmail.com', port: 587, imap_host: 'imap.gmail.com', imap_port: 993 },
  { id: 'outlook', label: 'Outlook / Office 365', host: 'smtp.office365.com', port: 587, imap_host: 'outlook.office365.com', imap_port: 993 },
  { id: 'yahoo', label: 'Yahoo Mail', host: 'smtp.mail.yahoo.com', port: 587, imap_host: 'imap.mail.yahoo.com', imap_port: 993 },
  { id: 'custom', label: 'Custom SMTP', host: '', port: 587, imap_host: '', imap_port: 993 },
];

function EmailAccountsTab() {
  const isAdminUser = useAuthStore((st) => st.user?.role === 'super_admin');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [showAddSMTP, setShowAddSMTP] = useState(false);
  const [smtpForm, setSMTPForm] = useState({
    label: '', email: '', provider: 'gmail_smtp', smtp_host: 'smtp.gmail.com', smtp_port: 587,
    imap_host: 'imap.gmail.com', imap_port: 993, username: '', password: '', use_tls: true,
  });
  const [saving, setSaving] = useState(false);

  const loadAccounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/email-accounts/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAccounts(data.accounts || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkGmailStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/email-accounts/gmail/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setGmailStatus(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
    checkGmailStatus();
  }, [loadAccounts, checkGmailStatus]);

  const handleConnectGmail = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/email-accounts/gmail/auth-url`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error(data.detail || 'Gmail OAuth not configured yet');
      }
    } catch {
      toast.error('Failed to start Gmail connection');
    }
  };

  const handleAddSMTP = async () => {
    if (!smtpForm.email || !smtpForm.username || !smtpForm.password) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/email-accounts/smtp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(smtpForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to add account');
      toast.success('SMTP account connected');
      setShowAddSMTP(false);
      setSMTPForm({
        label: '', email: '', provider: 'gmail_smtp', smtp_host: 'smtp.gmail.com', smtp_port: 587,
        imap_host: 'imap.gmail.com', imap_port: 993, username: '', password: '', use_tls: true,
      });
      loadAccounts();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSetPrimary = async (accountId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/email-accounts/set-primary/${accountId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Primary account updated');
      loadAccounts();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (accountId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/email-accounts/${accountId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Account removed');
      loadAccounts();
    } catch {
      toast.error('Failed to remove');
    }
  };

  const handleTestSMTP = async (accountId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/email-accounts/test-smtp/${accountId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Connection test passed');
      } else {
        toast.error(data.message || 'Connection test failed');
      }
    } catch {
      toast.error('Test failed');
    }
  };

  const handleProviderChange = (providerId) => {
    const provider = SMTP_PROVIDERS.find(p => p.id === providerId);
    if (provider) {
      setSMTPForm(prev => ({
        ...prev,
        provider: providerId,
        smtp_host: provider.host,
        smtp_port: provider.port,
        imap_host: provider.imap_host,
        imap_port: provider.imap_port,
      }));
    }
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="email-accounts-tab">
      <div className="bg-card border rounded-xl p-4"><GmailSyncButton /></div>
      {/* Gmail OAuth Section */}
      <div className="bg-card border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-lg">Connect Gmail</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Send emails directly from your Gmail account via OAuth 2.0
            </p>
          </div>
          <Button
            onClick={handleConnectGmail}
            className="gap-2"
            data-testid="connect-gmail-btn"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
              <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
            </svg>
            Connect Gmail
          </Button>
        </div>
        {gmailStatus && !gmailStatus.configured && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm">
            <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
            <span className="text-amber-700 dark:text-amber-400">
              Gmail OAuth requires Google API credentials. Once configured by the admin, you can connect your Gmail account here.
            </span>
          </div>
        )}
        {isAdminUser && <GoogleRedirectInfo />}
      </div>

      {/* SMTP/IMAP Accounts */}
      <div className="bg-card border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-lg">SMTP / IMAP Accounts</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Connect Outlook, Yahoo, or any SMTP provider for sending emails
            </p>
          </div>
          <Button variant="outline" onClick={() => setShowAddSMTP(true)} className="gap-2" data-testid="add-smtp-btn">
            <Plus className="h-4 w-4" /> Add Account
          </Button>
        </div>

        {/* Connected Accounts List */}
        {accounts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Mail className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No email accounts connected yet</p>
            <p className="text-xs mt-1">Connect a Gmail or SMTP account to start sending emails from the CRM</p>
          </div>
        ) : (
          <div className="space-y-3">
            {accounts.map(acc => (
              <div key={acc.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/30 transition-colors" data-testid={`email-account-${acc.id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${acc.type === 'gmail_oauth' ? 'bg-red-50 dark:bg-red-950/30' : 'bg-blue-50 dark:bg-blue-950/30'}`}>
                    <Mail className={`h-5 w-5 ${acc.type === 'gmail_oauth' ? 'text-red-500' : 'text-blue-500'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{acc.label || acc.email}</p>
                      {acc.is_primary && (
                        <span className="text-[10px] bg-primary/10 text-primary font-semibold px-2 py-0.5 rounded-full">PRIMARY</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {acc.email} &middot; {acc.type === 'gmail_oauth' ? 'Gmail OAuth' : `SMTP (${acc.provider})`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${acc.status === 'active' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                    <span className="text-xs text-muted-foreground capitalize">{acc.status}</span>
                  </div>
                  {!acc.is_primary && (
                    <Button variant="ghost" size="sm" onClick={() => handleSetPrimary(acc.id)} className="text-xs">
                      Set Primary
                    </Button>
                  )}
                  {acc.type === 'smtp' && (
                    <Button variant="ghost" size="sm" onClick={() => handleTestSMTP(acc.id)} className="text-xs" data-testid={`test-smtp-${acc.id}`}>
                      Test
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(acc.id)} className="text-destructive hover:text-destructive h-8 w-8" data-testid={`delete-account-${acc.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Campaign Rotation Info */}
      <div className="bg-card border rounded-xl p-6">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
          <div>
            <h4 className="font-medium text-sm">Campaign Email Rotation</h4>
            <p className="text-xs text-muted-foreground mt-1">
              Connect multiple email accounts to distribute outbound campaign volume across addresses.
              The system automatically rotates sending across your connected accounts to maintain healthy deliverability.
              Your primary account is used for direct 1:1 emails from lead profiles.
            </p>
          </div>
        </div>
      </div>

      {/* Add SMTP Dialog */}
      <Dialog open={showAddSMTP} onOpenChange={setShowAddSMTP}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Connect Email Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Provider Selection */}
            <div>
              <Label className="text-sm font-medium">Email Provider</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {SMTP_PROVIDERS.map(p => (
                  <label key={p.id} className={`flex items-center gap-2 p-2.5 border rounded-lg cursor-pointer transition-colors text-sm ${smtpForm.provider === p.id ? 'border-primary bg-primary/5' : 'hover:bg-accent/50'}`}>
                    <input type="radio" name="provider" value={p.id} checked={smtpForm.provider === p.id} onChange={() => handleProviderChange(p.id)} className="sr-only" />
                    <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${smtpForm.provider === p.id ? 'border-primary' : 'border-muted-foreground/40'}`}>
                      {smtpForm.provider === p.id && <div className="w-1.5 h-1.5 rounded-full bg-primary" />}
                    </div>
                    {p.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Account Label</Label>
                <Input placeholder="e.g. Work Outlook" value={smtpForm.label} onChange={e => setSMTPForm({...smtpForm, label: e.target.value})} className="mt-1" data-testid="smtp-label" />
              </div>
              <div>
                <Label className="text-sm">Email Address *</Label>
                <Input type="email" placeholder="you@company.com" value={smtpForm.email} onChange={e => setSMTPForm({...smtpForm, email: e.target.value})} className="mt-1" data-testid="smtp-email" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Username *</Label>
                <Input placeholder="Usually your email" value={smtpForm.username} onChange={e => setSMTPForm({...smtpForm, username: e.target.value})} className="mt-1" data-testid="smtp-username" />
              </div>
              <div>
                <Label className="text-sm">Password / App Password *</Label>
                <Input type="password" placeholder="App-specific password" value={smtpForm.password} onChange={e => setSMTPForm({...smtpForm, password: e.target.value})} className="mt-1" data-testid="smtp-password" />
              </div>
            </div>

            {smtpForm.provider === 'custom' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">SMTP Host</Label>
                  <Input placeholder="smtp.example.com" value={smtpForm.smtp_host} onChange={e => setSMTPForm({...smtpForm, smtp_host: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm">SMTP Port</Label>
                  <Input type="number" value={smtpForm.smtp_port} onChange={e => setSMTPForm({...smtpForm, smtp_port: parseInt(e.target.value)})} className="mt-1" />
                </div>
              </div>
            )}

            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <Shield className="h-3 w-3" /> Credentials are encrypted and stored securely. For Gmail, use an App Password from your Google Account settings.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSMTP(false)}>Cancel</Button>
            <Button onClick={handleAddSMTP} disabled={saving} data-testid="smtp-save-btn">
              {saving ? 'Connecting...' : 'Connect Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



/* ═══════════════════════════════════════════
   SECURITY TAB
   ═══════════════════════════════════════════ */
const jtiOf = (token) => { try { return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).jti; } catch { return null; } };
const deviceIcon = (ua = '') => (/iphone|android|mobile/i.test(ua) ? Smartphone : /mac|windows|linux/i.test(ua) ? Laptop : Monitor);
const deviceName = (ua = '') => {
  const os = /iphone/i.test(ua) ? 'iPhone' : /android/i.test(ua) ? 'Android' : /mac os/i.test(ua) ? 'Mac' : /windows/i.test(ua) ? 'Windows' : /linux/i.test(ua) ? 'Linux' : 'Device';
  const br = /edg\//i.test(ua) ? 'Edge' : /chrome\//i.test(ua) ? 'Chrome' : /firefox\//i.test(ua) ? 'Firefox' : /safari\//i.test(ua) ? 'Safari' : 'Browser';
  return `${os} - ${br}`;
};
const PWD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

function SecurityTab() {
  const [passwords, setPasswords] = useState({ current: '', newPwd: '', confirm: '' });
  const [showPwd, setShowPwd] = useState({});
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [mfa, setMfa] = useState(null);
  const [allSessions, setAllSessions] = useState(false);
  const myJti = jtiOf(localStorage.getItem('refresh_token') || '');
  const loadSessions = useCallback(() => api.request('/auth/sessions').then((r) => setSessions(r.sessions || [])).catch(() => {}), []);
  useEffect(() => { loadSessions(); api.request('/auth/mfa/status').then(setMfa).catch(() => setMfa({ mfa_enabled: false })); }, [loadSessions]);
  const shown = allSessions ? sessions : [...sessions].sort((a, b) => (b.refresh_jti === myJti) - (a.refresh_jti === myJti)).slice(0, 4);

  const handleChangePassword = async () => {
    if (passwords.newPwd !== passwords.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (!PWD_RULE.test(passwords.newPwd)) {
      toast.error('Use at least 8 characters with an uppercase letter, a lowercase letter and a number');
      return;
    }
    setSaving(true);
    try {
      const r = await api.request('/user-settings/change-password', {
        method: 'PUT',
        body: JSON.stringify({
          current_password: passwords.current,
          new_password: passwords.newPwd,
          refresh_token: localStorage.getItem('refresh_token'),
        }),
      });
      toast.success(r.other_sessions_ended ? `Password updated — signed out ${r.other_sessions_ended} other session${r.other_sessions_ended === 1 ? '' : 's'}` : 'Password updated');
      setPasswords({ current: '', newPwd: '', confirm: '' });
      loadSessions();
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    }
    setSaving(false);
  };

  const endOthers = async () => {
    try {
      const r = await api.request('/user-settings/sessions/end-others', { method: 'POST', body: JSON.stringify({ refresh_token: localStorage.getItem('refresh_token') }) });
      toast.success(`Signed out ${r.other_sessions_ended} other session${r.other_sessions_ended === 1 ? '' : 's'}`);
      loadSessions();
    } catch (err) { toast.error(err.message || 'Could not end other sessions'); }
  };

  const togglePwd = (field) => setShowPwd(prev => ({...prev, [field]: !prev[field]}));

  return (
    <div className="space-y-6" data-testid="security-tab">
      {/* Password & Authentication */}
      <div className="bg-card border rounded-xl">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Password & Authentication</h3>
          <p className="text-sm text-muted-foreground mt-1">Manage your password and two-factor authentication settings.</p>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { key: 'current', label: 'Current Password' },
              { key: 'newPwd', label: 'New Password' },
              { key: 'confirm', label: 'Confirm New Password' },
            ].map(field => (
              <div key={field.key}>
                <Label>{field.label}</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showPwd[field.key] ? 'text' : 'password'}
                    value={passwords[field.key]}
                    onChange={e => setPasswords({...passwords, [field.key]: e.target.value})}
                    className="pr-10"
                    data-testid={`pwd-${field.key}`}
                  />
                  <button
                    onClick={() => togglePwd(field.key)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    type="button"
                  >
                    {showPwd[field.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* 2FA Section */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-sm font-semibold">Two-Factor Authentication</h4>
                <p className="text-xs text-muted-foreground">Add an extra layer of security to your account</p>
              </div>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${mfa?.mfa_enabled ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-testid="mfa-status-pill">{mfa ? (mfa.mfa_enabled ? 'Enabled' : 'Not set up') : '…'}</span>
            </div>
            {mfa && <div className={`${mfa.mfa_enabled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'} border rounded-lg p-4 flex items-start gap-3`} data-testid="mfa-status">
              <Shield className={`h-5 w-5 mt-0.5 flex-shrink-0 ${mfa.mfa_enabled ? 'text-green-600' : 'text-amber-600'}`} />
              <div>
                <p className={`text-sm font-medium ${mfa.mfa_enabled ? 'text-green-800' : 'text-amber-800'}`}>{mfa.mfa_enabled ? '2FA is turned on' : '2FA is not set up yet'}</p>
                <p className={`text-xs mt-0.5 ${mfa.mfa_enabled ? 'text-green-700' : 'text-amber-700'}`}>{mfa.mfa_enabled ? `Sign-in codes go to ${mfa.method === 'email' ? `your email ${mfa.email_masked || ''}` : mfa.method === 'sms' ? `your phone ${mfa.phone_masked || ''}` : 'your authenticator app'}` : 'You will be asked to set it up during activation.'}</p>
              </div>
            </div>}
          </div>

          <div className="flex justify-end">
            <Button onClick={handleChangePassword} disabled={saving} data-testid="update-password-btn">
              <Lock className="h-4 w-4 mr-2" /> {saving ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      <div className="bg-card border rounded-xl">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Active Sessions</h3>
          <p className="text-sm text-muted-foreground mt-1">Manage your active sessions across different devices.</p>
        </div>
        <div className="p-6 space-y-3">
          {sessions.length === 0 && <p className="text-sm text-muted-foreground" data-testid="sessions-empty">No other sessions found.</p>}
          {shown.map((session, i) => {
            const Icon = deviceIcon(session.user_agent);
            const current = myJti && session.refresh_jti === myJti;
            return (
              <div key={i} className="flex items-center gap-4 p-3 rounded-lg border hover:bg-muted/20 transition-colors" data-testid={`session-${i}`}>
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{deviceName(session.user_agent)}</p>
                  <p className="text-xs text-muted-foreground">{session.ip_address} &bull; signed in {new Date(session.created_at).toLocaleString()}</p>
                </div>
                {current && <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-800" data-testid="session-current">This device</span>}
              </div>
            );
          })}
          {sessions.length > 4 && <button type="button" className="text-xs text-primary hover:underline" onClick={() => setAllSessions(!allSessions)} data-testid="sessions-toggle">{allSessions ? 'Show fewer' : `Show all ${sessions.length} sessions`}</button>}
          <p className="text-xs text-muted-foreground">Changing your password keeps this device signed in and signs out every other session. Other devices are fully signed out within 15 minutes.</p>
          <div className="pt-3 flex justify-end">
            <Button variant="outline" onClick={endOthers} disabled={sessions.length < 2} className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 text-xs" data-testid="end-other-sessions-btn">
              <LogOut className="h-3.5 w-3.5 mr-2" /> End All Other Sessions
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════
   PAYMENT METHODS TAB
   ═══════════════════════════════════════════ */
function PaymentTab() {
  const [banks, setBanks] = useState([]);
  const [cards, setCards] = useState([]);
  const [payoutSettings, setPayoutSettings] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [showCardDialog, setShowCardDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, c, ps, ph] = await Promise.all([
        api.request('/user-settings/bank-accounts'),
        api.request('/user-settings/credit-cards'),
        api.request('/user-settings/payout-settings'),
        api.request('/user-settings/payout-history'),
      ]);
      setBanks(b || []);
      setCards(c || []);
      setPayoutSettings(ps || {});
      setPayoutHistory(ph || []);
    } catch (err) {
      console.error('[UserSettings] Failed to load payout data:', err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setPrimary = async (type, id) => {
    try {
      await api.request(`/user-settings/${type}/${id}/set-primary`, { method: 'PUT' });
      toast.success('Primary updated');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Delete this payment method?')) return;
    try {
      await api.request(`/user-settings/${type}/${id}`, { method: 'DELETE' });
      toast.success('Deleted');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const savePayoutSettings = async () => {
    setSaving(true);
    try {
      await api.request('/user-settings/payout-settings', {
        method: 'PUT',
        body: JSON.stringify(payoutSettings),
      });
      toast.success('Payout settings saved');
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="payment-tab">
      {/* Bank Accounts */}
      <div className="bg-card border rounded-xl">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Bank Accounts</h3>
            <p className="text-sm text-muted-foreground mt-1">Manage your bank accounts for receiving residual payouts.</p>
          </div>
          <Button size="sm" onClick={() => setShowBankDialog(true)} data-testid="add-bank-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Bank Account
          </Button>
        </div>
        <div className="p-6">
          {banks.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="h-10 w-10 mx-auto text-muted-foreground/20 mb-2" />
              <p className="text-sm text-muted-foreground">No bank accounts added yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {banks.map(bank => (
                <div key={bank.id} className="flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/20 transition-colors" data-testid={`bank-${bank.id}`}>
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold">{bank.bank_name}</p>
                      <span className="text-xs text-muted-foreground">{bank.account_number_masked}</span>
                    </div>
                    <p className="text-xs text-muted-foreground capitalize">
                      {bank.account_type} {bank.is_primary && '- Primary for residual payments'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {bank.is_primary && (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-800">Primary</span>
                    )}
                    {bank.status === 'verified' && (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-100 text-blue-800">Verified</span>
                    )}
                    {!bank.is_primary && (
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => setPrimary('bank-accounts', bank.id)}>
                        Set as Primary
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteItem('bank-accounts', bank.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Credit Cards */}
      <div className="bg-card border rounded-xl">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Credit Cards</h3>
            <p className="text-sm text-muted-foreground mt-1">Manage your credit cards for purchases and expenses.</p>
          </div>
          <Button size="sm" onClick={() => setShowCardDialog(true)} data-testid="add-card-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Credit Card
          </Button>
        </div>
        <div className="p-6">
          {cards.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="h-10 w-10 mx-auto text-muted-foreground/20 mb-2" />
              <p className="text-sm text-muted-foreground">No credit cards added yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cards.map(card => (
                <div key={card.id} className="flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/20 transition-colors" data-testid={`card-${card.id}`}>
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                    <CreditCard className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold">{card.card_brand}</p>
                      <span className="text-xs text-muted-foreground">****{card.last_four}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Expires {card.expiry} &bull; {card.cardholder_name}
                      {card.is_primary && ' - Primary for purchases'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {card.is_primary && (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-800">Primary</span>
                    )}
                    {!card.is_primary && (
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => setPrimary('credit-cards', card.id)}>
                        Set as Primary
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteItem('credit-cards', card.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Payout Settings */}
      {payoutSettings && (
        <div className="bg-card border rounded-xl">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Payout Settings</h3>
            <p className="text-sm text-muted-foreground mt-1">Configure how and when you receive your residual payments.</p>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Minimum Payout Amount</Label>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={payoutSettings.min_payout_amount || 50}
                    onChange={e => setPayoutSettings({...payoutSettings, min_payout_amount: parseFloat(e.target.value) || 0})}
                    className="pl-7"
                    data-testid="payout-min-amount"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Payouts below this amount will be held until threshold is reached</p>
              </div>
              <div>
                <Label>Payout Frequency</Label>
                <select
                  value={payoutSettings.payout_frequency || 'monthly'}
                  onChange={e => setPayoutSettings({...payoutSettings, payout_frequency: e.target.value})}
                  className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  data-testid="payout-frequency"
                >
                  <option value="monthly">Monthly</option>
                  <option value="bi-weekly">Bi-weekly</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={payoutSettings.auto_payout ?? true}
                  onChange={e => setPayoutSettings({...payoutSettings, auto_payout: e.target.checked})}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  data-testid="payout-auto"
                />
                <div>
                  <Label className="cursor-pointer">Auto-Payout</Label>
                  <p className="text-xs text-muted-foreground">Automatically transfer when threshold is met</p>
                </div>
              </div>
              <div>
                <Label>Preferred Payout Day</Label>
                <select
                  value={payoutSettings.preferred_payout_day || '15th of each month'}
                  onChange={e => setPayoutSettings({...payoutSettings, preferred_payout_day: e.target.value})}
                  className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  data-testid="payout-day"
                >
                  <option value="15th of each month">15th of each month</option>
                  <option value="1st of each month">1st of each month</option>
                  <option value="last day of month">Last day of month</option>
                </select>
              </div>
            </div>

            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-800">Payout Information</p>
                <p className="text-xs text-blue-700 mt-0.5">Payouts are processed within 1-3 business days after the scheduled date. Ensure your primary bank account is verified to avoid delays.</p>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPayoutSettings({ min_payout_amount: 50, payout_frequency: 'monthly', auto_payout: true, preferred_payout_day: '15th of each month' })}>
                Reset to Defaults
              </Button>
              <Button onClick={savePayoutSettings} disabled={saving} data-testid="save-payout-btn">
                <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Payout History */}
      <div className="bg-card border rounded-xl">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Recent Payouts</h3>
            <p className="text-sm text-muted-foreground mt-1">View your last 5 payout transactions.</p>
          </div>
          <Button variant="link" className="text-primary text-xs p-0 h-auto" data-testid="view-all-payouts">View All</Button>
        </div>
        <div className="overflow-x-auto">
          {payoutHistory.length === 0 ? (
            <div className="p-8 text-center">
              <Clock className="h-10 w-10 mx-auto text-muted-foreground/20 mb-2" />
              <p className="text-sm text-muted-foreground">No payout history yet.</p>
            </div>
          ) : (
            <table className="w-full" data-testid="payout-history-table">
              <thead>
                <tr className="border-b">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">Date</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">Amount</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">Account</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {payoutHistory.map((p, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-muted/20">
                    <td className="p-4 text-sm">{new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td className="p-4 text-sm font-medium">${(p.amount || 0).toFixed(2)}</td>
                    <td className="p-4 text-sm text-muted-foreground">{p.account}</td>
                    <td className="p-4">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                        p.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>{p.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add Bank Account Dialog */}
      <AddBankDialog open={showBankDialog} onClose={() => setShowBankDialog(false)} onSuccess={load} />
      {/* Add Credit Card Dialog */}
      <AddCardDialog open={showCardDialog} onClose={() => setShowCardDialog(false)} onSuccess={load} />
    </div>
  );
}


/* ── Add Bank Account Dialog ─────────────────────────────── */
function AddBankDialog({ open, onClose, onSuccess }) {
  const [form, setForm] = useState({ bank_name: '', account_type: 'checking', routing_number: '', account_number: '', is_primary: false });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.bank_name || !form.routing_number || !form.account_number) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      await api.request('/user-settings/bank-accounts', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      toast.success('Bank account added');
      onClose();
      onSuccess();
      setForm({ bank_name: '', account_type: 'checking', routing_number: '', account_number: '', is_primary: false });
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="add-bank-dialog">
        <DialogHeader>
          <DialogTitle>Add Bank Account</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Bank Name *</Label>
            <Input value={form.bank_name} onChange={e => setForm({...form, bank_name: e.target.value})} className="mt-1" placeholder="Chase Bank" data-testid="bank-name-input" />
          </div>
          <div>
            <Label>Account Type</Label>
            <select value={form.account_type} onChange={e => setForm({...form, account_type: e.target.value})} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" data-testid="bank-type-select">
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>
          <div>
            <Label>Routing Number *</Label>
            <Input value={form.routing_number} onChange={e => setForm({...form, routing_number: e.target.value})} className="mt-1" placeholder="021000021" data-testid="bank-routing-input" />
          </div>
          <div>
            <Label>Account Number *</Label>
            <Input value={form.account_number} onChange={e => setForm({...form, account_number: e.target.value})} className="mt-1" placeholder="123456789" data-testid="bank-account-input" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.is_primary} onChange={e => setForm({...form, is_primary: e.target.checked})} className="h-4 w-4 rounded border-gray-300 text-primary" data-testid="bank-primary-check" />
            <Label className="cursor-pointer">Set as primary account</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-bank-btn">
            {saving ? 'Adding...' : 'Add Bank Account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ── Add Credit Card Dialog ──────────────────────────────── */
function AddCardDialog({ open, onClose, onSuccess }) {
  const [form, setForm] = useState({ card_brand: 'Visa', last_four: '', expiry: '', cardholder_name: '', is_primary: false });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.last_four || !form.expiry || !form.cardholder_name) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      await api.request('/user-settings/credit-cards', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      toast.success('Credit card added');
      onClose();
      onSuccess();
      setForm({ card_brand: 'Visa', last_four: '', expiry: '', cardholder_name: '', is_primary: false });
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="add-card-dialog">
        <DialogHeader>
          <DialogTitle>Add Credit Card</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Card Brand</Label>
            <select value={form.card_brand} onChange={e => setForm({...form, card_brand: e.target.value})} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" data-testid="card-brand-select">
              <option value="Visa">Visa</option>
              <option value="Mastercard">Mastercard</option>
              <option value="Amex">American Express</option>
              <option value="Discover">Discover</option>
            </select>
          </div>
          <div>
            <Label>Last 4 Digits *</Label>
            <Input value={form.last_four} onChange={e => setForm({...form, last_four: e.target.value.replace(/\D/g, '').slice(0, 4)})} className="mt-1" placeholder="1234" maxLength={4} data-testid="card-last4-input" />
          </div>
          <div>
            <Label>Expiry *</Label>
            <Input value={form.expiry} onChange={e => setForm({...form, expiry: e.target.value})} className="mt-1" placeholder="MM/YYYY" data-testid="card-expiry-input" />
          </div>
          <div>
            <Label>Cardholder Name *</Label>
            <Input value={form.cardholder_name} onChange={e => setForm({...form, cardholder_name: e.target.value})} className="mt-1" placeholder="John Doe" data-testid="card-holder-input" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.is_primary} onChange={e => setForm({...form, is_primary: e.target.checked})} className="h-4 w-4 rounded border-gray-300 text-primary" data-testid="card-primary-check" />
            <Label className="cursor-pointer">Set as primary card</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-card-btn">
            {saving ? 'Adding...' : 'Add Credit Card'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ═══════════════════════════════════════════
   NOTIFICATIONS TAB
   ═══════════════════════════════════════════ */
function NotificationsTab() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.request('/user-settings/notifications');
        setPrefs(data);
      } catch { setPrefs({ residual_payouts: true, monthly_statements: true, account_updates: false }); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.request('/user-settings/notifications', {
        method: 'PUT',
        body: JSON.stringify(prefs),
      });
      toast.success('Notification preferences saved');
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  if (!prefs) return null;

  const toggles = [
    { key: 'residual_payouts', label: 'New residual payouts', desc: 'Get notified when new residual payments are available' },
    { key: 'monthly_statements', label: 'Monthly statements', desc: 'Receive monthly statement summaries via email' },
    { key: 'account_updates', label: 'Account updates', desc: 'Get notified about changes to your account settings' },
  ];

  return (
    <div className="space-y-6">
    <NotificationChannelsCard />
    <div className="bg-card border rounded-xl" data-testid="notifications-tab">
      <div className="p-6 border-b">
        <h3 className="text-lg font-semibold">Notification Preferences</h3>
        <p className="text-sm text-muted-foreground mt-1">Manage how you receive notifications and updates.</p>
      </div>
      <div className="p-6 space-y-6">
        <div>
          <h4 className="text-sm font-semibold mb-4">Email Notifications</h4>
          <div className="space-y-4">
            {toggles.map(t => (
              <div key={t.key} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/20 transition-colors">
                <div>
                  <p className="text-sm font-medium">{t.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t.desc}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={prefs[t.key] ?? false}
                    onChange={e => setPrefs({...prefs, [t.key]: e.target.checked})}
                    className="sr-only peer"
                    data-testid={`notif-toggle-${t.key}`}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} data-testid="save-notifications-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </div>
    </div>
    </div>
  );
}
